import countriesJson from 'src/utils/countries.json';

const ISO_COUNTRIES = countriesJson as Array<{ name: string; code: string }>;

/** Shortest country name we accept inside a longer region label (see step 4). */
const MIN_CONTAINED_NAME = 4;

/** Labels the ISO list cannot express, or spells differently. Checked first, so
 *  they win over every other rule:
 *  - "Europe" is the historical one: the list carries an "Eu" row, which the old
 *    substring match reached from any "...Europe..." label. Kept, word-bounded,
 *    so "Deutschland" no longer resolves to the EU flag.
 *  - "UK" is how the region is written; the ISO code is "gb".
 *  - "Northern Ireland" would otherwise match the "Ireland" it contains. */
const LABEL_ALIASES: Array<[RegExp, string]> = [
    [/^uk$/, 'gb'],
    [/^northern ireland$/, 'gb'],
    [/\beurope(an union)?\b/, 'eu'],
];

/** 2-letter labels that read as a multi-country region far more often than as the
 *  ISO country sharing the code: "NA" is North America, not Namibia. No flag beats
 *  a confidently wrong one — and these labels showed none before this resolver
 *  existed, the old branch returning an uppercase code Iconify has no icon for. */
const REGION_LIKE_CODES = new Set(['na', 'sa', 'me', 'la', 'af', 'as']);

export type BqCountry = { country_name?: string; country_code?: string };

/** One row of the workspace's region mapping (`country_region_mapping`). */
export type RegionMapping = { region?: string; countries?: string[] };

/**
 * Flag code (lowercase ISO 3166-1 alpha-2) for a region label, or null when the
 * label is not a country — aggregates such as "EMEA" then render no flag.
 *
 * Workspaces name their Google Ads regions freely: some use the country name
 * ("United States"), others the ISO code itself ("US", "CA" — Hunter Douglas).
 * The code case MUST be resolved before any name matching: a 2-letter label is a
 * substring of dozens of country names, which is how "US" used to resolve to
 * Australia ("aUStralia") and "CA" to American Samoa ("ameriCAn samoa").
 */
export const resolveFlagCode = (
    regionName?: string | null,
    bqCountries?: BqCountry[] | null
): string | null => {
    const name = regionName?.trim().toLowerCase();
    if (!name) return null;

    // 1. Labels with an agreed flag, whatever the ISO list says.
    const alias = LABEL_ALIASES.find(([pattern]) => pattern.test(name));
    if (alias) return alias[1];

    // 2. The region IS an ISO code. Only real, unambiguous codes pass, so neither
    //    a "CE" region (no such flag) nor a "NA" one (North America) gets a flag.
    if (name.length === 2) {
        return ISO_COUNTRIES.some((c) => c.code === name) && !REGION_LIKE_CODES.has(name)
            ? name
            : null;
    }

    // 3. Exact country name — the curated list first: it is the audited source
    //    (the BQ sheet has known-bad rows, e.g. Brunei → "ba" = Bosnia), and it
    //    answers on the first render, where the sheet-backed query is slow.
    //    The sheet then covers the names the curated list does not carry.
    const isoExact = ISO_COUNTRIES.find((c) => c.name.toLowerCase() === name);
    if (isoExact) return isoExact.code;
    const bqExact = bqCountries?.find((c) => c.country_name?.toLowerCase() === name);
    if (bqExact?.country_code) return bqExact.country_code.toLowerCase();

    // 4. Region label built around a country name ("United States - West").
    //    Longest match wins, and only this direction: a country name containing
    //    the label is the substring trap described above. The length floor keeps
    //    2-letter rows ("Eu") from matching inside any word.
    let best: { name: string; code: string } | null = null;
    const consider = (candidate?: string, code?: string) => {
        if (!candidate || !code || candidate.length < MIN_CONTAINED_NAME) return;
        if (best && candidate.length <= best.name.length) return;
        if (name.includes(candidate.toLowerCase())) best = { name: candidate, code };
    };
    bqCountries?.forEach((c) => consider(c.country_name, c.country_code));
    ISO_COUNTRIES.forEach((c) => consider(c.name, c.code));

    return best ? (best as { code: string }).code.toLowerCase() : null;
};

/**
 * Flag code for a region, read from the workspace's own region mapping rather
 * than from the region's name: a region covering exactly one country flies that
 * country's flag, a region covering several flies none.
 *
 * This is the only thing that tells "NA" = Namibia (one country) from "NA" =
 * North America (three) — the label alone cannot. Regions the mapping does not
 * cover fall back to reading the label, which is what workspaces without any
 * region mapping rely on (there, region_name IS the country name).
 */
export const resolveRegionFlagCode = (
    regionName?: string | null,
    regionMapping?: RegionMapping[] | null,
    bqCountries?: BqCountry[] | null
): string | null => {
    const name = regionName?.trim().toLowerCase();
    if (!name) return null;

    const mapped: string[] = [];
    (regionMapping ?? []).forEach((m) => {
        if (m.region?.trim().toLowerCase() !== name) return;
        (m.countries ?? []).forEach((c) => {
            const country = c?.trim();
            if (country && !mapped.includes(country)) mapped.push(country);
        });
    });

    if (mapped.length === 1) return resolveFlagCode(mapped[0], bqCountries);
    if (mapped.length > 1) return null;
    return resolveFlagCode(regionName, bqCountries);
};
